/**
 * HOPE Tab Component
 * Enhanced HOPE assessment dashboard for RN Command Center
 * Integrates with the new /api/hope-clinical endpoints
 */

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Activity, AlertOctagon, Clock, CheckCircle, Users,
  AlertTriangle, Bell, Shield, TrendingUp, Brain,
  Calendar, Play, Eye, RefreshCw, ClipboardCheck, Stethoscope
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const HOPETab = ({
  isDark,
  hopeDashboard,
  setSelectedPatient,
  setActiveTab
}) => {
  const [hopeView, setHopeView] = useState("rn-view");
  const [loading, setLoading] = useState(true);
  const [executiveData, setExecutiveData] = useState(null);
  const [rnData, setRnData] = useState(null);
  const [idgData, setIdgData] = useState(null);
  const [sfvAlerts, setSfvAlerts] = useState([]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const fetchHOPEData = async () => {
    setLoading(true);
    try {
      const [execRes, rnRes, idgRes, sfvRes] = await Promise.all([
        fetch(`${API_URL}/api/hope-clinical/dashboard/executive`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/hope-clinical/dashboard/rn-command-center`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/hope-clinical/dashboard/idg`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/hope-clinical/sfv-alerts?status=active`, { headers: getAuthHeaders() })
      ]);

      if (execRes.ok) setExecutiveData(await execRes.json());
      if (rnRes.ok) setRnData(await rnRes.json());
      if (idgRes.ok) setIdgData(await idgRes.json());
      if (sfvRes.ok) {
        const data = await sfvRes.json();
        setSfvAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to fetch HOPE data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchHOPEData();
    const interval = setInterval(fetchHOPEData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Combined stats from both old and new data sources
  const stats = {
    compliant: rnData?.summary?.green_compliant || hopeDashboard?.summary?.compliant || 0,
    atRisk: rnData?.summary?.yellow_at_risk || hopeDashboard?.summary?.due_soon || 0,
    overdue: rnData?.summary?.red_overdue || hopeDashboard?.summary?.overdue || 0,
    complianceRate: executiveData?.summary?.hope_compliance_rate || hopeDashboard?.summary?.compliance_rate || 100,
    sfvPending: sfvAlerts?.length || 0
  };

  return (
    <>
      {/* Summary Cards */}
      <div className="grid md:grid-cols-5 gap-3 mb-4">
        <Card className={isDark ? "bg-green-900/20 border-green-600/30" : "bg-green-50 border-green-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-500">{stats.compliant}</p>
            <p className={`text-sm ${isDark ? "text-green-300" : "text-green-600"}`}>Compliant</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-amber-900/20 border-amber-600/30" : "bg-amber-50 border-amber-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-500">{stats.atRisk}</p>
            <p className={`text-sm ${isDark ? "text-amber-300" : "text-amber-600"}`}>At Risk</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-red-900/20 border-red-600/30" : "bg-red-50 border-red-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-500">{stats.overdue}</p>
            <p className={`text-sm ${isDark ? "text-red-300" : "text-red-600"}`}>Overdue</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-purple-900/20 border-purple-600/30" : "bg-purple-50 border-purple-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-500">{stats.sfvPending}</p>
            <p className={`text-sm ${isDark ? "text-purple-300" : "text-purple-600"}`}>SFV Pending</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-teal-900/20 border-teal-600/30" : "bg-teal-50 border-teal-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-teal-500">{stats.complianceRate?.toFixed(0) || 0}%</p>
            <p className={`text-sm ${isDark ? "text-teal-300" : "text-teal-600"}`}>Compliance</p>
          </CardContent>
        </Card>
      </div>

      {/* Sub-tabs for different views */}
      <Tabs value={hopeView} onValueChange={setHopeView} className="space-y-4">
        <TabsList className={isDark ? "bg-slate-800 border-slate-700" : "bg-white border"}>
          <TabsTrigger value="rn-view" className="flex items-center gap-2">
            <Stethoscope className="w-4 h-4" />
            RN View
          </TabsTrigger>
          <TabsTrigger value="sfv-queue" className="flex items-center gap-2 relative">
            <Bell className="w-4 h-4" />
            SFV Queue
            {stats.sfvPending > 0 && (
              <Badge className="ml-1 bg-red-500 text-white text-xs">{stats.sfvPending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="idg-review" className="flex items-center gap-2">
            <Brain className="w-4 h-4" />
            IDG Review
          </TabsTrigger>
          <TabsTrigger value="schedule" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Schedule
          </TabsTrigger>
        </TabsList>

        {/* RN View - Patient Status */}
        <TabsContent value="rn-view" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Red/Overdue Patients */}
            <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-red-500">
                  <AlertOctagon className="w-5 h-5" />
                  Action Required
                </CardTitle>
                <CardDescription>Overdue or SFV pending patients</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {(rnData?.patients?.red?.length > 0 || hopeDashboard?.overdue_patients?.length > 0) ? (
                    <div className="space-y-2">
                      {(rnData?.patients?.red || hopeDashboard?.overdue_patients || []).map((p) => (
                        <div
                          key={p.patient_id}
                          className={`p-3 rounded-lg border ${isDark ? "border-red-800 bg-red-900/10" : "border-red-200 bg-red-50"}`}
                          data-testid={`hope-red-${p.patient_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{p.patient_name || p.patient_id}</p>
                              <p className="text-xs text-red-500">
                                {p.next_action || p.days_overdue ? `${p.days_overdue} days overdue` : 'Action Required'}
                              </p>
                              {p.sfv_pending && (
                                <Badge className="mt-1 bg-red-600 text-xs">SFV Required</Badge>
                              )}
                            </div>
                            <Button 
                              size="sm" 
                              className="bg-red-600 hover:bg-red-700"
                              onClick={() => { 
                                setSelectedPatient({ patient_id: p.patient_id, patient_name: p.patient_name }); 
                                setActiveTab("voice"); 
                              }}
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Document
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-center py-8 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                      <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                      <p>No urgent actions required</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Yellow/At Risk Patients */}
            <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-amber-500">
                  <Clock className="w-5 h-5" />
                  Due Soon
                </CardTitle>
                <CardDescription>Assessments due in next 3 days</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {(rnData?.patients?.yellow?.length > 0 || hopeDashboard?.due_soon_patients?.length > 0) ? (
                    <div className="space-y-2">
                      {(rnData?.patients?.yellow || hopeDashboard?.due_soon_patients || []).map((p) => (
                        <div
                          key={p.patient_id}
                          className={`p-3 rounded-lg border ${isDark ? "border-amber-800 bg-amber-900/10" : "border-amber-200 bg-amber-50"}`}
                          data-testid={`hope-yellow-${p.patient_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{p.patient_name || p.patient_id}</p>
                              <p className="text-xs text-amber-500">
                                {p.next_action || 'Due Soon'}
                              </p>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-amber-500 text-amber-700"
                              onClick={() => { 
                                setSelectedPatient({ patient_id: p.patient_id, patient_name: p.patient_name }); 
                                setActiveTab("voice"); 
                              }}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-center py-8 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                      <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                      <p>No upcoming assessments</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* SFV Queue */}
        <TabsContent value="sfv-queue" className="space-y-4">
          <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-red-500">
                <Bell className="w-5 h-5" />
                SFV Queue - Symptom Follow-up Visits Required
              </CardTitle>
              <CardDescription>
                Complete SFV within 2 calendar days of symptom trigger
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {sfvAlerts?.length > 0 ? (
                  <div className="space-y-3">
                    {sfvAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={`p-4 rounded-lg border ${
                          alert.urgency === 'critical' 
                            ? (isDark ? "border-red-700 bg-red-900/20" : "border-red-300 bg-red-50")
                            : alert.urgency === 'high'
                            ? (isDark ? "border-amber-700 bg-amber-900/20" : "border-amber-300 bg-amber-50")
                            : (isDark ? "border-slate-600 bg-slate-800/50" : "border-slate-200 bg-slate-50")
                        }`}
                        data-testid={`sfv-alert-${alert.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium">{alert.patient_name || alert.patient_id}</p>
                            <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                              Triggered by: {alert.triggering_visit_id}
                            </p>
                          </div>
                          <Badge className={
                            alert.urgency === 'critical' ? 'bg-red-500' :
                            alert.urgency === 'high' ? 'bg-amber-500' : 'bg-blue-500'
                          }>
                            {alert.hours_until_due}h remaining
                          </Badge>
                        </div>

                        <div className="mb-3">
                          <p className={`text-xs font-medium ${isDark ? "text-slate-400" : "text-slate-500"} mb-1`}>
                            Triggering Symptoms
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {alert.triggering_symptoms?.map((s, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {s.symptom_id}: {s.severity_label}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        <div className="flex justify-between items-center">
                          <p className={`text-xs ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                            Due: {new Date(alert.due_date).toLocaleString()}
                          </p>
                          <Button 
                            size="sm" 
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => {
                              setSelectedPatient({ 
                                patient_id: alert.patient_id, 
                                patient_name: alert.patient_name,
                                sfv_triggering_visit: alert.triggering_visit_id
                              });
                              setActiveTab("voice");
                            }}
                          >
                            <ClipboardCheck className="w-3 h-3 mr-1" />
                            Complete SFV
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-8 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p className="font-medium">No pending SFV alerts</p>
                    <p className={`text-sm ${isDark ? "text-slate-600" : "text-slate-400"}`}>
                      All symptom follow-ups are complete
                    </p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* IDG Review */}
        <TabsContent value="idg-review" className="space-y-4">
          <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-purple-500">
                <Brain className="w-5 h-5" />
                IDG Review - Patients with Active Moderate/Severe Symptoms
              </CardTitle>
              <CardDescription>
                {idgData?.patients_requiring_review || 0} patients require IDG discussion
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {idgData?.idg_review_list?.length > 0 ? (
                  <div className="space-y-3">
                    {idgData.idg_review_list.map((patient, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border ${isDark ? "border-slate-600 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium">{patient.patient_name || patient.patient_id}</p>
                            <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                              Last Visit: {patient.last_visit_type} on {patient.last_visit_date}
                            </p>
                          </div>
                          {patient.sfv_triggered && !patient.sfv_completed && (
                            <Badge className="bg-red-500">SFV Required</Badge>
                          )}
                        </div>

                        <div className="mb-3">
                          <p className={`text-xs font-medium ${isDark ? "text-slate-400" : "text-slate-500"} mb-2`}>
                            Active Symptoms
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {patient.active_symptoms?.map((s, sidx) => (
                              <Badge 
                                key={sidx}
                                className={s.severity_label?.includes('Severe') ? 'bg-red-500' : 'bg-amber-500'}
                              >
                                {s.symptom_id}: {s.severity_label}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {patient.discussion_prompts?.length > 0 && (
                          <div className={`p-2 rounded ${isDark ? "bg-purple-900/20" : "bg-purple-50"} border ${isDark ? "border-purple-800" : "border-purple-200"}`}>
                            <p className={`text-xs font-medium ${isDark ? "text-purple-300" : "text-purple-700"} mb-1`}>
                              IDG Discussion Prompts
                            </p>
                            <ul className={`text-xs ${isDark ? "text-purple-400" : "text-purple-600"} space-y-1`}>
                              {patient.discussion_prompts.map((prompt, pidx) => (
                                <li key={pidx}>• {prompt}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-8 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p className="font-medium">No patients with active moderate/severe symptoms</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedule Reference */}
        <TabsContent value="schedule" className="space-y-4">
          <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">CMS HOPE Timeline Requirements</CardTitle>
              <CardDescription>Required visits within first 30 days of admission</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                <div className={`p-4 rounded-lg ${isDark ? "bg-blue-900/20 border border-blue-800" : "bg-blue-50 border border-blue-200"}`}>
                  <p className="font-semibold text-blue-500 text-lg">ADM</p>
                  <p className="text-sm font-medium">Admission Visit</p>
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"} mt-2`}>Day 0 - Required</p>
                  <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"} mt-1`}>Initial HOPE assessment at start of care</p>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? "bg-purple-900/20 border border-purple-800" : "bg-purple-50 border border-purple-200"}`}>
                  <p className="font-semibold text-purple-500 text-lg">HUV1</p>
                  <p className="text-sm font-medium">Update Visit 1</p>
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"} mt-2`}>Day 6-15 - Required</p>
                  <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"} mt-1`}>First scheduled update assessment</p>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? "bg-indigo-900/20 border border-indigo-800" : "bg-indigo-50 border border-indigo-200"}`}>
                  <p className="font-semibold text-indigo-500 text-lg">HUV2</p>
                  <p className="text-sm font-medium">Update Visit 2</p>
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"} mt-2`}>Day 16-30 - Required</p>
                  <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"} mt-1`}>Second scheduled update assessment</p>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? "bg-red-900/20 border border-red-800" : "bg-red-50 border border-red-200"}`}>
                  <p className="font-semibold text-red-500 text-lg">SFV</p>
                  <p className="text-sm font-medium">Symptom Follow-up</p>
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"} mt-2`}>Within 2 days of trigger</p>
                  <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"} mt-1`}>Triggered by moderate/severe symptoms</p>
                </div>
              </div>

              <div className={`mt-6 p-4 rounded-lg ${isDark ? "bg-amber-900/20 border border-amber-800" : "bg-amber-50 border border-amber-200"}`}>
                <p className="font-semibold text-amber-600 mb-2">SFV Trigger Criteria (J2051)</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>Symptoms Assessed:</p>
                    <ul className={`text-sm ${isDark ? "text-slate-400" : "text-slate-600"} mt-1 space-y-1`}>
                      <li>• Pain</li>
                      <li>• Shortness of Breath</li>
                      <li>• Anxiety</li>
                      <li>• Nausea</li>
                      <li>• Vomiting</li>
                      <li>• Diarrhea</li>
                      <li>• Constipation</li>
                      <li>• Agitation</li>
                    </ul>
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>SFV Triggers When:</p>
                    <ul className={`text-sm ${isDark ? "text-slate-400" : "text-slate-600"} mt-1 space-y-1`}>
                      <li>• Pain score ≥ 4/10</li>
                      <li>• Any symptom = Moderate (code 2)</li>
                      <li>• Any symptom = Severe (code 3)</li>
                    </ul>
                    <p className={`text-xs ${isDark ? "text-amber-400" : "text-amber-600"} mt-3`}>
                      <strong>Note:</strong> SFV is a SEPARATE visit form and cannot be combined with HUV visits.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
};

export default HOPETab;
